import { COMPARISON, MAX_PERIOD_DAYS } from '../GscTools/period';
import { emptyDraft, resolveScope } from '../GscTools/scope';
import { SCOPE_KEYS, hasScope, scopeDraftFromParams, scopeParamsFromScope } from '../GscTools/url';
import { DEFAULT_THRESHOLDS, PRESET } from './thresholds';

/**
 * The scope this report runs under, and the criteria it then reviews the result against.
 *
 * They are deliberately two different things with two different Apply buttons. The scope decides
 * what is asked of Search Console — one property, one window, one surface — and changing it costs
 * a request. The thresholds decide which of the returned rows are worth a look, and changing them
 * costs nothing, because they are read over rows already in memory. A single Apply would have
 * hidden that difference and charged a request for moving a slider.
 *
 * No comparison is offered. Every figure on this report is read inside one window, so a baseline
 * would be a control that changes nothing — and a period-over-period claim is exactly the kind of
 * thing a striking-distance queue is most likely to be misread as making.
 */
export function initialDraft(period) {
    return {
        ...emptyDraft(period),
        // Fixed rather than hidden-but-settable: resolveScope reads this, and "none" is what
        // makes it stop resolving a baseline nothing on the page would use.
        comparison: COMPARISON.NONE,
        preset: PRESET.NEARER_TOP,
        ...DEFAULT_THRESHOLDS,
    };
}

/**
 * The scope a request is built from.
 *
 * The thresholds are not part of it on purpose: they never reach Search Console, and putting them
 * in the resolved scope would imply the rows came back already filtered by them.
 */
export function resolveReportScope(draft, t) {
    return resolveScope(draft, t, { maxDays: MAX_PERIOD_DAYS });
}

/** The query-string keys this report owns on top of the shared scope ones. */
const REPORT_KEYS = ['preset', 'min-position', 'max-position', 'min-impressions', 'max-ctr'];

export const URL_KEYS = [...SCOPE_KEYS, ...REPORT_KEYS];

/** Whether the address describes a report to run, counting this report's own criteria too. */
export function hasReportScope(params) {
    return hasScope(params, REPORT_KEYS);
}

const readNumber = (value, { min, max }) => {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return null;
    if (parsed < min || parsed > max) return null;
    return parsed;
};

/**
 * The draft this report starts from when its address already describes one.
 *
 * Each criterion is range-checked on the way in. A query string is typed by hand as easily as it
 * is copied, and a candidate list selected by a value the user cannot see in the controls would
 * be a queue nobody could account for.
 */
export function reportDraftFromParams(params) {
    const draft = scopeDraftFromParams(params);

    if (Object.values(PRESET).includes(params.preset)) draft.preset = params.preset;

    const minPosition = readNumber(params['min-position'], { min: 1, max: 1000 });
    const maxPosition = readNumber(params['max-position'], { min: 1, max: 1000 });
    // Only taken as a pair, and only the right way round: half a band, or a reversed one, would
    // select a different set of rows than the link meant.
    if (minPosition !== null && maxPosition !== null && minPosition <= maxPosition) {
        draft.minPosition = minPosition;
        draft.maxPosition = maxPosition;
    }

    const minImpressions = readNumber(params['min-impressions'], { min: 0, max: 1e9 });
    if (minImpressions !== null) draft.minImpressions = minImpressions;

    const maxCtr = readNumber(params['max-ctr'], { min: 0, max: 1 });
    if (maxCtr !== null) draft.maxCtr = maxCtr;

    return draft;
}

/** The applied scope and criteria as query parameters. */
export function reportParamsFromScope(scope, thresholds) {
    return {
        ...scopeParamsFromScope(scope),
        preset: thresholds.preset,
        'min-position': thresholds.minPosition,
        'max-position': thresholds.maxPosition,
        'min-impressions': thresholds.minImpressions,
        'max-ctr': thresholds.preset === PRESET.LOW_CTR ? thresholds.maxCtr : undefined,
    };
}
